import React, { useState } from 'react'
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Typography,
  TextField,
  MenuItem,
  Grid,
  IconButton,
  Collapse,
  Alert
} from '@mui/material'
import {
  KeyboardArrowDown as ExpandMoreIcon,
  KeyboardArrowUp as ExpandLessIcon
} from '@mui/icons-material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import type { ServiceType, ServiceStatus } from '../../types/integration'

interface HealthCheckRecord {
  id: string
  service_type: ServiceType
  provider_name: string
  status: ServiceStatus
  response_time_ms?: number
  checked_at: string
  error_message?: string
  error_details?: Record<string, any>
}

interface HealthHistoryTableProps {
  records: HealthCheckRecord[]
  totalRecords: number
  onFilterChange: (filters: HealthHistoryFilters) => void
}

export interface HealthHistoryFilters {
  service_type?: ServiceType
  status?: ServiceStatus
  date_from?: string
  date_to?: string
  page: number
  page_size: number
}

const HealthHistoryTable: React.FC<HealthHistoryTableProps> = ({
  records,
  totalRecords,
  onFilterChange
}) => {
  const [filters, setFilters] = useState<HealthHistoryFilters>({
    page: 0,
    page_size: 20
  })
  const [expandedRow, setExpandedRow] = useState<string | null>(null)

  const handleFilterChange = (key: keyof HealthHistoryFilters, value: any) => {
    const newFilters = { ...filters, [key]: value, page: 0 }
    setFilters(newFilters)
    onFilterChange(newFilters)
  }

  const handlePageChange = (_event: unknown, newPage: number) => {
    const newFilters = { ...filters, page: newPage }
    setFilters(newFilters)
    onFilterChange(newFilters)
  }

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newFilters = { ...filters, page_size: parseInt(event.target.value, 10), page: 0 }
    setFilters(newFilters)
    onFilterChange(newFilters)
  }

  const handleRowExpand = (recordId: string) => {
    setExpandedRow(expandedRow === recordId ? null : recordId)
  }

  const getStatusColor = (status: ServiceStatus): 'success' | 'error' | 'default' | 'warning' => {
    switch (status) {
      case 'active':
        return 'success'
      case 'error':
        return 'error'
      case 'inactive':
        return 'default'
      case 'maintenance':
        return 'warning'
      default:
        return 'default'
    }
  }

  const formatDateTime = (dateString: string): string => {
    const date = new Date(dateString)
    return date.toLocaleString()
  }

  const formatResponseTime = (ms?: number): string => {
    if (ms === undefined || ms === null) return 'N/A'
    if (ms < 1000) return `${ms}ms`
    return `${(ms / 1000).toFixed(2)}s`
  }

  return (
    <Box>
      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Filter Health Check History
        </Typography>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                fullWidth
                label="Service Type"
                value={filters.service_type || ''}
                onChange={(e) => handleFilterChange('service_type', e.target.value || undefined)}
                size="small"
              >
                <MenuItem value="">All Types</MenuItem>
                <MenuItem value="email">Email</MenuItem>
                <MenuItem value="whatsapp">WhatsApp</MenuItem>
                <MenuItem value="sms">SMS</MenuItem>
                <MenuItem value="payment">Payment</MenuItem>
                <MenuItem value="logistics">Logistics</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                fullWidth
                label="Status"
                value={filters.status || ''}
                onChange={(e) => handleFilterChange('status', e.target.value || undefined)}
                size="small"
              >
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="error">Error</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
                <MenuItem value="maintenance">Maintenance</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <DatePicker
                label="From Date"
                value={filters.date_from ? new Date(filters.date_from) : null}
                onChange={(date) => handleFilterChange('date_from', date?.toISOString())}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <DatePicker
                label="To Date"
                value={filters.date_to ? new Date(filters.date_to) : null}
                onChange={(date) => handleFilterChange('date_to', date?.toISOString())}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </Grid>
          </Grid>
        </LocalizationProvider>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width={50} />
              <TableCell>Service Type</TableCell>
              <TableCell>Provider</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Response Time</TableCell>
              <TableCell>Checked At</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {records.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="textSecondary" py={4}>
                    No health check records found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              records.map((record) => (
                <React.Fragment key={record.id}>
                  <TableRow hover>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleRowExpand(record.id)}
                        disabled={!record.error_message && !record.error_details}
                      >
                        {expandedRow === record.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" textTransform="capitalize">
                        {record.service_type}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{record.provider_name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={record.status.toUpperCase()}
                        color={getStatusColor(record.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatResponseTime(record.response_time_ms)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDateTime(record.checked_at)}
                      </Typography>
                    </TableCell>
                  </TableRow>

                  {/* Expandable Error Details Row */}
                  {(record.error_message || record.error_details) && (
                    <TableRow>
                      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                        <Collapse in={expandedRow === record.id} timeout="auto" unmountOnExit>
                          <Box sx={{ margin: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              Error Details
                            </Typography>
                            {record.error_message && (
                              <Alert severity="error" sx={{ mb: 1 }}>
                                {record.error_message}
                              </Alert>
                            )}
                            {record.error_details && (
                              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                                <pre style={{ margin: 0, fontSize: '0.875rem', overflow: 'auto' }}>
                                  {JSON.stringify(record.error_details, null, 2)}
                                </pre>
                              </Paper>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>

        {/* Pagination */}
        <TablePagination
          rowsPerPageOptions={[10, 20, 50, 100]}
          component="div"
          count={totalRecords}
          rowsPerPage={filters.page_size}
          page={filters.page}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </TableContainer>
    </Box>
  )
}

export default HealthHistoryTable
